import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useBrand } from "@/contexts/brand-context";
import { useSubscription } from "@/contexts/subscription-context";
import { pb } from "@/lib/pocketbase";
import {
  faArrowRight,
  faFileImport,
  faHashtag,
  faList,
  faMagicWandSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ArticleProgressModal from "@/components/articles/ArticleProgressModal";
import ListicleModal from "@/components/articles/ListicleModal";
import { generateArticle } from "@/services/articleGeneration";

// Custom spinner component
const Spinner = ({ size = "lg" }: { size?: string }) => (
  <div
    className={`animate-spin rounded-full border-2 border-current border-t-transparent ${
      size === "lg" ? "h-8 w-8" : "h-4 w-4"
    }`}
  />
);

// Define the Topic interface
interface Topic {
  topic: string;
  keyword: string;
  selected?: boolean;
}

interface ImportedTopic {
  keyword: string;
  title: string;
  selected: boolean;
}

export default function NewArticle() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get("onboarding") === "true";
  const { currentBrand } = useBrand();
  const { subscription, hasAvailableQuota, isFreePlan } = useSubscription();
  const [topicsModalOpen, setTopicsModalOpen] = useState(false);
  const [keywordModalOpen, setKeywordModalOpen] = useState(false);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessasge] = useState(
    "Generating recommendations...",
  );
  const [currentPostId, setCurrentPostId] = useState<string | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importedTopics, setImportedTopics] = useState<ImportedTopic[]>([]);
  const [keyword, setKeyword] = useState("");
  const [topic, setTopic] = useState("");
  const [listicleModalOpen, setListicleModalOpen] = useState(false);

  // Auto-open AI recommendations modal if coming from onboarding
  useEffect(() => {
    if (isOnboarding) {
      fetchTopics();
      setTopicsModalOpen(true);
    }
  }, [isOnboarding]);

  // setCurrentPostId("vmt2p2r90642w66");

  // Function to fetch topics from the API
  const fetchTopics = async () => {
    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    setLoading(true);
    try {
      // Call the API endpoint we created
      const response = await fetch(
        `${pb.baseURL}/api/v1/brands/topics?brandId=${currentBrand.id}&count=10`,
        {
          headers: {
            Authorization: pb.authStore.token,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch topics");
      }

      const data = await response.json();

      // Add a selected property to each topic
      const topicsWithSelection = data.topics.map((topic: Topic) => ({
        ...topic,
        selected: false,
      }));

      setTopics(topicsWithSelection);
    } catch (error) {
      console.error("Error fetching topics:", error);
      toast.error("Failed to fetch topic recommendations");
    } finally {
      setLoading(false);
    }
  };

  // Handle topic selection - only one topic can be selected at a time
  const handleTopicSelection = (index: number) => {
    const updatedTopics = [...topics].map((topic, i) => ({
      ...topic,
      selected: i === index ? !topic.selected : false,
    }));

    setTopics(updatedTopics);

    // Update selected topics
    setSelectedTopics(updatedTopics.filter((topic) => topic.selected));
  };

  // Shared function to generate and create an article
  const generateAndCreateArticle = async ({
    keyword = "",
    topic = "",
    loadingMessage = "Generating article...",
    closeModal = () => {},
  }: {
    keyword?: string;
    topic?: string;
    loadingMessage?: string;
    closeModal: () => void;
  }) => {
    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    // Check quota before proceeding
    if (!hasAvailableQuota()) {
      // Check if this is after the first article
      const totalArticles = subscription?.articlesQuota.used || 0;

      if (totalArticles === 0) {
        // This shouldn't happen as first article should be allowed, but show appropriate message
        toast.error(
          "Unable to create your first article. Please try again or contact support.",
        );
      } else if (isFreePlan) {
        toast.error(
          "You've used your free article! Upgrade now to continue creating content.",
        );
      } else {
        toast.error(
          "You've reached your monthly article limit. Please upgrade your plan for more articles.",
        );
      }
      closeModal();
      return;
    }

    // Show loading state
    setLoading(true);
    setLoadingMessasge(loadingMessage);

    try {
      // Call our post generation API using the service
      const response = await generateArticle({
        brandId: currentBrand.id,
        keyword: keyword || undefined,
        topic: topic || undefined,
      });

      if (response.success && response.data?.postId) {
        // Start progress tracking
        setCurrentPostId(response.data.postId);

        // Close modal and show progress
        closeModal();
        setLoading(false);
      } else {
        throw new Error("Failed to start article generation");
      }
    } catch (error) {
      console.error("Error creating article:", error);
      toast.error("Failed to create article");
      closeModal();
    } finally {
      setLoading(false);
    }
  };

  // Handle create article with keyword and optional topic
  const handleCreateByKeyword = async () => {
    if (!keyword.trim()) {
      toast.error("Please enter a keyword");
      return;
    }

    // Check quota before proceeding
    if (!hasAvailableQuota()) {
      toast.error(
        "You've reached your monthly article limit. Please upgrade your plan for more articles.",
      );
      return;
    }

    setLoading(true);
    try {
      // Call our post generation API using the service
      const response = await generateArticle({
        brandId: currentBrand?.id || "",
        topic: keyword,
      });

      if (response.success && response.data?.postId) {
        // Start progress tracking
        setCurrentPostId(response.data.postId);

        // Close the keyword modal
        setKeywordModalOpen(false);
        setLoading(false);
      } else {
        throw new Error("Failed to start article generation");
      }
    } catch (error) {
      console.error("Error generating post:", error);
      toast.error("Failed to generate article. Please try again.");
      setKeywordModalOpen(false);
      setLoading(false);
    }
  };

  // Handle bulk import
  const handleImport = () => {
    if (!importText.trim()) {
      toast.error("Please enter some keywords");
      return;
    }

    // Check quota before proceeding
    if (!hasAvailableQuota()) {
      toast.error(
        "You've reached your monthly article limit. Please upgrade your plan for more articles.",
      );
      return;
    }

    // Split by newlines and filter out empty lines
    const keywords = importText
      .split("\n")
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    if (keywords.length === 0) {
      toast.error("No valid keywords found");
      return;
    }

    // Initialize topics with empty titles
    const topics = keywords.map((keyword) => ({
      keyword,
      title: "",
      selected: true,
    }));

    setImportedTopics(topics);
    generateTitles(topics);
  };

  const generateTitles = async (topics: ImportedTopic[]) => {
    setLoading(true);
    try {
      if (!currentBrand) {
        throw new Error("No brand selected");
      }

      // Check quota before proceeding
      if (!hasAvailableQuota()) {
        toast.error(
          "You've reached your monthly article limit. Please upgrade your plan for more articles.",
        );
        return;
      }

      // Extract keywords from topics
      const keywords = topics.map((topic) => topic.keyword);

      // Call the new generate-topics endpoint
      const response = await fetch(`${pb.baseURL}/api/v1/seo/generate-topics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify({
          brandId: currentBrand.id,
          keywords: keywords,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to generate titles");
      }

      const responseData = await response.json();
      // Map the response data to our topics based on the specific format
      // The API returns { data: [ { keyword: string, title: string }, ... ] }
      let updatedTopics;

      // Define the response type
      interface TopicResponseItem {
        keyword: string;
        title: string;
      }

      // Check if responseData has a data property with an array of objects
      if (responseData.data && Array.isArray(responseData.data)) {
        // Create a map of keyword -> title from the response
        const titleMap = new Map<string, string>();
        responseData.data.forEach((item: TopicResponseItem) => {
          if (item.keyword && item.title) {
            titleMap.set(item.keyword, item.title);
          }
        });

        // Map our topics to include the titles from the response
        updatedTopics = topics.map((topic) => ({
          ...topic,
          title: titleMap.get(topic.keyword) || topic.keyword,
        }));
        setImportedTopics(updatedTopics);
      }
    } catch (error) {
      console.error("Error generating titles:", error);
      toast.error("Failed to generate titles");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setImportedTopics((prev) =>
      prev.map((topic) => ({
        ...topic,
        selected: checked,
      })),
    );
  };

  const handleSelectTopic = (index: number, checked: boolean) => {
    setImportedTopics((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: checked };
      return updated;
    });
  };

  const handleAddToLibrary = async () => {
    if (!currentBrand) {
      toast.error("Please select a brand first");
      return;
    }

    const selectedTopics = importedTopics.filter((topic) => topic.selected);

    if (selectedTopics.length === 0) {
      toast.error("Please select at least one topic");
      return;
    }

    // Check quota before proceeding
    // For bulk imports, we need to check if there's enough quota for all selected topics
    if (selectedTopics.length > 0) {
      const remainingQuota = subscription?.articlesQuota.remaining || 0;

      if (
        subscription?.articlesQuota.isLimited &&
        selectedTopics.length > remainingQuota
      ) {
        toast.error(
          `You can only create ${remainingQuota} more articles this month. Please reduce your selection or upgrade your plan.`,
        );
        return;
      }

      // Also check the basic quota availability
      if (!hasAvailableQuota()) {
        // Don't block, just inform that topics will be distributed
        toast.info(
          "Monthly quota reached. Topics will be automatically distributed across future months.",
        );
      }
    }

    setLoading(true);
    try {
      // Validate and format topics for the API
      const topicsForAPI = selectedTopics.map((topic) => {
        // Ensure topic has valid data
        if (!topic.title || topic.title.trim().length === 0) {
          throw new Error("Some topics have invalid data. Please check your selection.");
        }
        return {
          topic: topic.title.trim(),
          keyword: topic.keyword?.trim() || "",
        };
      });

      // Call the content schedule API
      const response = await fetch(`${pb.baseURL}/api/v1/content/schedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify({
          brandId: currentBrand.id,
          topics: topicsForAPI,
        }),
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        responseData = { message: "Server returned an invalid response" };
      }

      if (!response.ok) {
        // Handle specific error cases with user-friendly messages
        if (response.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        } else if (response.status === 403) {
          throw new Error(responseData.message || "You don't have permission to schedule content for this brand.");
        } else if (response.status === 400) {
          // Handle specific bad request errors
          if (responseData.message?.includes("quota")) {
            toast.info("Topics will be distributed across available months based on your quota.");
            // Don't throw error, let it proceed
          } else if (responseData.message?.includes("subscription")) {
            throw new Error("No active subscription found. Please subscribe to schedule content.");
          } else {
            throw new Error(responseData.message || "Invalid request. Please check your input.");
          }
        } else if (response.status === 404) {
          throw new Error("Brand not found or you don't have access to it.");
        } else {
          throw new Error(responseData.message || "Failed to schedule content. Please try again.");
        }
      }

      // Show success message with distribution info if available
      const successMessage = responseData.count
        ? `Successfully scheduled ${responseData.count} topics`
        : `Added ${selectedTopics.length} topics to your content schedule`;
      
      toast.success(successMessage);

      // Close the modal and reset state
      setImportedTopics([]);
      setImportText("");
      setImportModalOpen(false);
    } catch (error) {
      console.error("Error scheduling content:", error);
      
      // Show user-friendly error message
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle create article with selected topics
  const handleCreateArticle = async () => {
    if (selectedTopics.length === 0) {
      toast.error("Please select at least one topic");
      return;
    }

    setLoading(true);
    // try {
    //   // Create articles for selected topics
    //   for (const topic of selectedTopics) {
    //     await pb.collection("posts").create({
    //       brand: currentBrand.id,
    //       title: topic.title,
    //       content: "",
    //       status: PostsStatusOptions.Draft,
    //       keyword: topic.keyword,
    //     });
    //   }

    //   toast.success(`Added ${selectedTopics.length} articles to your library`);
    //   setImportModalOpen(false);
    //   navigate("/articles");
    // } catch (error) {
    //   console.error("Error creating articles:", error);
    //   toast.error("Failed to add articles to library");
    // } finally {
    //   setLoading(false);
    // }
    // For now, just use the first selected topic to create an article
    const selectedTopic = selectedTopics[0];

    await generateAndCreateArticle({
      topic: selectedTopic.topic,
      keyword: selectedTopic.keyword,
      closeModal: () => setTopicsModalOpen(false),
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          {isOnboarding ? "Create Your First Article" : "Create New Article"}
        </h2>
        <p className="text-muted-foreground">
          Choose how you'd like to create your new content.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="glass-card neon-border hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faMagicWandSparkles}
                className="h-5 w-5 text-black"
              />
              AI Recommended
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Let our AI suggest content ideas based on your audience and past
              performance.
            </p>
            <Button
              onClick={() => {
                fetchTopics();
                setTopicsModalOpen(true);
                // setCurrentPostId("vmt2p2r90642w66");
              }}
              className="w-full bg-black hover:bg-black/90"
              disabled={!hasAvailableQuota()}
              title={
                !hasAvailableQuota()
                  ? subscription?.articlesQuota.used === 0
                    ? "Unable to create article"
                    : "You've reached your monthly article limit"
                  : ""
              }
            >
              Get Recommendations
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-card neon-border hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faHashtag}
                className="h-5 w-5 text-black"
              />
              Create by Keyword
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Start with a specific keyword and we'll help you optimize around
              it.
            </p>
            <Button
              onClick={() => setKeywordModalOpen(true)}
              className="w-full bg-black hover:bg-black/90"
              disabled={!hasAvailableQuota()}
              title={
                !hasAvailableQuota()
                  ? subscription?.articlesQuota.used === 0
                    ? "Unable to create article"
                    : "You've reached your monthly article limit"
                  : ""
              }
            >
              Enter Keyword
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-card neon-border hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faFileImport}
                className="h-5 w-5 text-black"
              />
              Bulk Import
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Import multiple keywords at once and generate articles in bulk.
            </p>
            <Button
              onClick={() => setImportModalOpen(true)}
              className="w-full bg-black hover:bg-black/90"
              disabled={!hasAvailableQuota()}
              title={
                !hasAvailableQuota()
                  ? subscription?.articlesQuota.used === 0
                    ? "Unable to create article"
                    : "You've reached your monthly article limit"
                  : ""
              }
            >
              Import Keywords
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-card neon-border hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faList}
                className="h-5 w-5 text-black"
              />
              Create Listicle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Generate a comprehensive list-style article with research, screenshots, and detailed comparisons.
            </p>
            <Button
              onClick={() => setListicleModalOpen(true)}
              className="w-full bg-black hover:bg-black/90"
              disabled={!hasAvailableQuota()}
              title={
                !hasAvailableQuota()
                  ? subscription?.articlesQuota.used === 0
                    ? "Unable to create article"
                    : "You've reached your monthly article limit"
                  : ""
              }
            >
              Create Listicle
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Topics Recommendation Modal */}
      <Dialog open={topicsModalOpen} onOpenChange={setTopicsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recommended Topics</DialogTitle>
            <DialogDescription>
              Select a topic for your new article.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Spinner size="lg" />
              <span className="ml-2">{loadingMessage}</span>
            </div>
          ) : topics.length > 0 ? (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {topics.map((topic, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-2 p-2 rounded hover:bg-slate-50"
                  >
                    <Checkbox
                      id={`topic-${index}`}
                      checked={topic.selected}
                      onCheckedChange={() => handleTopicSelection(index)}
                    />
                    {/* Radio-like behavior - only one can be selected */}
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor={`topic-${index}`} className="font-medium">
                        {topic.topic}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Keyword: {topic.keyword}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No topic recommendations available. Try again later.
            </div>
          )}

          <DialogFooter className="flex justify-between items-center">
            <Button
              onClick={handleCreateArticle}
              disabled={selectedTopics.length === 0 || loading}
              className="bg-black hover:bg-black/90"
            >
              {loading ? (
                <>
                  <Spinner size="sm" />
                  <span className="ml-2">Generating...</span>
                </>
              ) : (
                <>
                  Generate
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="mr-2 h-4 w-4"
                  />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Keyword Modal */}
      <Dialog open={keywordModalOpen} onOpenChange={setKeywordModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create by Keyword</DialogTitle>
            <DialogDescription>
              Enter a keyword and optional topic for your new article.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="keyword" className="font-medium">
                Keyword <span className="text-red-500">*</span>
              </Label>
              <Input
                id="keyword"
                placeholder="Enter main keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="col-span-3"
              />
              <p className="text-sm text-muted-foreground">
                The primary keyword your article will target.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="topic" className="font-medium">
                Topic (Optional)
              </Label>
              <Input
                id="topic"
                placeholder="Enter topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="col-span-3"
              />
              <p className="text-sm text-muted-foreground">
                A broader topic to help contextualize your keyword.
              </p>
            </div>
          </div>

          <DialogFooter className="flex justify-between items-center">
            <Button
              onClick={handleCreateByKeyword}
              disabled={!keyword.trim() || loading}
              className="bg-black hover:bg-black/90"
            >
              {loading ? (
                <>
                  <Spinner size="sm" />
                  <span className="ml-2">Generating...</span>
                </>
              ) : (
                <>
                  Generate Article
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="mr-2 h-4 w-4"
                  />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Keywords</DialogTitle>
            <DialogDescription>
              Enter keywords (one per line) to generate multiple articles at
              once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {importedTopics.length === 0 ? (
              <div>
                <Label htmlFor="import">Keywords</Label>
                <Textarea
                  id="import"
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="Enter keywords, one per line"
                  className="min-h-[200px]"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all"
                      checked={importedTopics.every((topic) => topic.selected)}
                      onCheckedChange={handleSelectAll}
                    />
                    <Label htmlFor="select-all">Select All</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {importedTopics.filter((topic) => topic.selected).length}/
                    {importedTopics.length} topics
                  </p>
                </div>

                <div className="border rounded-lg">
                  <ScrollArea className="h-[300px]">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 w-8"></th>
                          <th className="text-left py-2 px-3">Keyword</th>
                          <th className="text-left py-2 px-3">
                            SEO Optimized Title
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedTopics.map((topic, index) => (
                          <tr
                            key={index}
                            className="border-b last:border-b-0 hover:bg-slate-50"
                          >
                            <td className="py-2 px-3">
                              <Checkbox
                                id={`topic-${index}`}
                                checked={topic.selected}
                                onCheckedChange={(checked) =>
                                  handleSelectTopic(index, checked as boolean)
                                }
                              />
                            </td>
                            <td className="py-2 px-3">
                              <Label
                                htmlFor={`topic-${index}`}
                                className="font-medium text-sm"
                              >
                                {topic.keyword}
                              </Label>
                            </td>
                            <td className="py-2 px-3">
                              <p className="text-sm text-muted-foreground">
                                {topic.title || "Generating title..."}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => {
                setImportModalOpen(false);
                setImportText("");
                setImportedTopics([]);
              }}
            >
              Cancel
            </Button>
            <div className="space-x-2">
              {importedTopics.length === 0 ? (
                <Button
                  onClick={handleImport}
                  disabled={loading || !importText.trim()}
                  className="bg-black hover:bg-black/90"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" />
                      <span className="ml-2">Generating...</span>
                    </>
                  ) : (
                    "Generate Titles"
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleAddToLibrary}
                  disabled={
                    loading || !importedTopics.some((topic) => topic.selected)
                  }
                  className="bg-black hover:bg-black/90"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" />
                      <span className="ml-2">Adding to Library...</span>
                    </>
                  ) : (
                    "Add Selected to Library"
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Listicle Modal */}
      <ListicleModal
        isOpen={listicleModalOpen}
        onClose={() => setListicleModalOpen(false)}
        onArticleStarted={(postId) => {
          // Start progress tracking
          setCurrentPostId(postId);
        }}
      />

      {/* Article Progress Modal */}
      <ArticleProgressModal
        postId={currentPostId}
        onComplete={(postId) => {
          // On completion, navigate to edit page
          toast.success("Article generated successfully!");
          navigate(`/articles/${postId}`);
        }}
        onClose={() => {
          navigate("/articles");
          setCurrentPostId(null);
        }}
      />
    </div>
  );
}
